import React, {useState} from 'react';
import {Modal, NumberInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {DateInput} from '@mantine/dates';
import {Button} from 'src/design/atoms/Button';

export interface ComplianceBulkExtendDialogProps {
  opened: boolean;
  selectedCount: number;
  isSaving: boolean;
  onClose: () => void;
  onConfirm: (payload: {extendByDays?: number; newDueAt?: Date}) => void;
}

/**
 * Extend-due-date dialog for the compliance bulk bar. The two inputs are
 * mutually exclusive — filling one clears the other — and confirm is
 * disabled until exactly one is set.
 */
export const ComplianceBulkExtendDialog: React.FC<
  ComplianceBulkExtendDialogProps
> = ({opened, selectedCount, isSaving, onClose, onConfirm}) => {
  const [extendByDays, setExtendByDays] = useState<number | ''>('');
  const [newDueAt, setNewDueAt] = useState<Date | null>(null);

  const handleClose = () => {
    setExtendByDays('');
    setNewDueAt(null);
    onClose();
  };

  const handleExtendByDaysChange = (value: number | string) => {
    setExtendByDays(value === '' ? '' : Number(value));
    if (value !== '') {
      setNewDueAt(null);
    }
  };

  const handleNewDueAtChange = (value: Date | null) => {
    setNewDueAt(value);
    if (value) {
      setExtendByDays('');
    }
  };

  const canConfirm =
    (extendByDays !== '' && extendByDays > 0) || newDueAt !== null;

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }
    onConfirm({
      extendByDays: extendByDays !== '' ? extendByDays : undefined,
      newDueAt: newDueAt ?? undefined,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Extend due date for ${selectedCount} assignment${selectedCount === 1 ? '' : 's'}?`}
      centered>
      <Text size="sm" c="grey" mb="lg">
        Choose either a number of days to extend by, or an explicit new due
        date. Assignments keep their manual extension when future rule
        recalculation runs.
      </Text>
      <Group align="flex-end" mb="lg" wrap="wrap">
        <Box style={{width: '200px'}}>
          <NumberInput
            label="Extend by (days)"
            placeholder="e.g. 14"
            min={1}
            value={extendByDays}
            onChange={handleExtendByDaysChange}
            disabled={newDueAt !== null}
          />
        </Box>
        <Box style={{width: '200px'}}>
          <DateInput
            label="New due date"
            placeholder="Select date"
            value={newDueAt}
            onChange={handleNewDueAtChange}
            disabled={extendByDays !== ''}
            clearable
          />
        </Box>
      </Group>
      <Group justify="end">
        <Button variant="subtle" color="black" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="filled"
          color="yellow"
          onClick={handleConfirm}
          disabled={!canConfirm || isSaving}
          loading={isSaving}>
          Extend
        </Button>
      </Group>
    </Modal>
  );
};
